import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ErrorBoundary } from '@sentry/react'
import { Resizable } from 're-resizable'

import { VideoPlayer, VideoPlayerRef } from '@/components/ui/video'
import { useDocumentState } from '@/state/document'
import {
  VideoTemplate,
  TemplateAttribute,
  useUpdateTemplateElement,
} from '@/services/hooks/template_attributes'
import { cn } from '@/utils'
import { Spinner } from '@/components/ui/spinner'
import { useAnnotationLayer } from './Annotations/queries'
import { getAbsoluteYFromSubPage } from './Annotations/utils'
import { DocumentPage } from '@/lib/api/client'
import { useAnnotationsContext } from './Annotations/AnnotationsContext'
import { useRoute } from 'wouter'
import { VideoIcon } from '@/components/icons/VideoIcon'
import {
  DEFAULT_VIEW_HEIGHT,
  DEFAULT_VIEW_WIDTH,
} from '@/pages/CADPage/constants'
import { OptionsContextMenu } from '@/components/core/OptionsContextMenu'
import { useListVideos } from '@/services/queries/videos'
import { useKeyboardNudge } from '@/utils/hooks/useKeyboardNudge'
import { useMultiSelectDraggable } from '@/pages/DocumentPage/features/templateAttributesMultiSelect/useMultiSelectDraggable'
import { useRelativeToAbsolutePositioning } from '@/utils/hooks/useRelativeToAbsolutePositioning'
import { useMultiSelectHandler } from '../features/templateAttributesMultiSelect/useMultiSelectHandler'

type DraggableVideoProps = {
  template: TemplateAttribute<VideoTemplate>
  isReadOnly?: boolean
  documentPage: DocumentPage
  dataType: 'video'
  onNudgeStart?: (showGrid: boolean) => void
  onNudgeEnd?: (hideGrid: boolean) => void
}

export const DraggableVideo = ({
  template,
  isReadOnly,
  documentPage,
  dataType,
  onNudgeStart,
  onNudgeEnd,
}: DraggableVideoProps) => {
  const INITIAL_DELTA = useMemo(() => ({ x: 0, y: 0 }), [])
  const videoRef = useRef<VideoPlayerRef | null>(null)
  const zoom = useDocumentState((state) => state.zoom)
  const zoomRatio = useMemo(() => zoom / 100, [zoom])
  const [isPrintPage] = useRoute(/print$/)
  const selectElement = useDocumentState((state) => state.setSelectedElement)
  const selectedElement = useDocumentState((state) => state.selectedElement)
  const croppingImageId = useDocumentState((state) => state.croppingImageId)
  const videoId = useMemo(() => template?.template_values.videoId, [template])
  const { data: videos } = useListVideos({
    documentVersionId: documentPage.document_version as string,
  })
  const videoData = useMemo(
    () => videos?.find((video) => video.id === videoId),
    [videos, videoId],
  )
  const downloadUrl = useMemo(
    () => videoData?.download_url || '',
    [videoData?.download_url],
  )
  const documentPageId = useMemo(
    () => documentPage.id as string,
    [documentPage],
  )
  const isSelected = useMemo(
    () =>
      selectedElement?.documentPageId === documentPageId &&
      selectedElement?.id === template.id,
    [selectedElement, documentPageId, template.id],
  )
  const {
    bringForward,
    bringToFront,
    sendBackward,
    sendToBack,
    isLoading: isOptionsMenuLoading,
  } = useAnnotationLayer(documentPageId)
  const isAddingElement = useDocumentState((state) => state.isAddingElement)
  const [aspectRatio, setAspectRatio] = useState(0)
  const { setIsResizing } = useAnnotationsContext()
  const [{ position, delta }, setPosition] = useState({
    position: {
      x: 0,
      y: 0,
    },
    delta: INITIAL_DELTA,
  })
  const [containerDimensions, setContainerDimensions] = useState({
    width: 0,
    height: 0,
  })
  const subPageId = useMemo(
    () => template?.template_values?.subPageId || documentPageId,
    [template],
  )
  const documentSubPages = useMemo(
    () =>
      (documentPage?.template_values?.subPages as Array<string>) ?? [
        documentPage?.id,
      ],
    [documentPage],
  )
  const positionData = useMemo(
    () => template?.template_values?.position,
    [template],
  )
  const videoSize = useMemo(() => template?.template_values?.size, [template])
  const { mutateAsync: updateTemplate } = useUpdateTemplateElement({
    documentPageId,
  })

  const {
    onPointerDown: multiSelectKeydownHandler,
    onPointerUp: multiSelectKeyupHandler,
  } = useMultiSelectHandler()

  const onVideoLoaded = useCallback((width, height) => {
    const aspectRatio = width / height
    setAspectRatio(aspectRatio)
    setContainerDimensions({
      width: Math.max(200 * aspectRatio, 150),
      height: 200,
    })
  }, [])

  const { attributes, isDragging, listeners, transform, setNodeRef } =
    useMultiSelectDraggable({
      disabled: isAddingElement || isReadOnly || !!croppingImageId,
      id: template?.id as string,
      data: { type: dataType },
    })

  const { bind, previewDelta } = useKeyboardNudge({
    isActive: isSelected && Boolean(selectedElement?.isRemovable),
    showGridOnNudge: true,
    onNudgeStart,
    onNudgeEnd,
    template,
    documentPage,
  })

  const updateVideoSizeHandler = useCallback(
    async (
      size: { width: number; height: number },
      position: { x: number; y: number },
    ) => {
      await updateTemplate({
        payload: {
          size,
          position: { ...position, z: positionData?.z },
        },
        data_type: dataType,
        templateAttributeId: template?.id as string,
      })
    },
    [dataType, template?.id, positionData, updateTemplate],
  )

  useEffect(() => {
    if (!videoSize) return
    setContainerDimensions(videoSize)
  }, [videoSize])

  useEffect(() => {
    const tX = transform?.x ?? 0
    const tY = transform?.y ?? 0
    const pX = previewDelta?.x ?? 0
    const pY = previewDelta?.y ?? 0

    // If everything is 0, this is typically the "drag ended" / "idle" state.
    // Don't overwrite delta here — let it be reset only when positionData changes.
    if (tX === 0 && tY === 0 && pX === 0 && pY === 0) {
      return
    }

    setPosition((prevPosition) => ({
      ...prevPosition,
      delta: {
        x: (tX + pX) / zoomRatio,
        y: (tY + pY) / zoomRatio,
      },
    }))
  }, [transform, previewDelta, zoomRatio])

  useEffect(() => {
    if (!positionData) return
    setPosition({
      position: positionData,
      delta: INITIAL_DELTA,
    })
  }, [INITIAL_DELTA, positionData])

  const onVideoPlayerClick = useCallback(() => {
    if (isSelected) return

    setTimeout(() => {
      selectElement({
        documentPageId,
        id: template?.id as string,
        isRemovable: true,
        type: dataType,
      })
    }, 100)
  }, [isSelected, selectElement, documentPageId, template])

  const { setDraggableAnnotationParentRef } = useRelativeToAbsolutePositioning()

  return isPrintPage ? (
    <VideoIcon
      strokeWidth={'2%'}
      style={{
        position: 'absolute',
        top: template.template_values.position?.y || 0,
        left: template.template_values.position?.x || 0,
        width: template.template_values.size?.width || DEFAULT_VIEW_WIDTH,
        height: template.template_values.size?.height || DEFAULT_VIEW_HEIGHT,
      }}
    />
  ) : (
    <OptionsContextMenu
      isReadonly={isReadOnly}
      isDisabled={isOptionsMenuLoading}
      optionsGroups={[
        {
          options: [
            {
              label: 'Bring to front',
              action: () => bringToFront(template?.id as string),
            },
            {
              label: 'Bring forward',
              action: () => bringForward(template?.id as string),
            },
            {
              label: 'Send backward',
              action: () => sendBackward(template?.id as string),
            },
            {
              label: 'Send to back',
              action: () => sendToBack(template?.id as string),
            },
          ],
        },
      ]}
    >
      <div
        {...bind}
        {...attributes}
        {...listeners}
        className={cn(
          'border rounded-md flex flex-1 p-px box-content absolute',
          {
            'cursor-move': !isReadOnly,
            'cursor-default': isReadOnly,
            'shadow-md': isDragging,
            'shadow-gray-200': isDragging,
            'bg-sky-200/10': isDragging,
            'z-[100]': isDragging,
          },
        )}
        style={{
          translate: `${delta?.x || 0}px ${delta?.y || 0}px`,
          top: getAbsoluteYFromSubPage({
            subPageId,
            relativeY: position?.y || 0,
            subPageIds: documentSubPages,
          }),
          left: position?.x || 0,
          zIndex: positionData?.z || 1000,
          borderColor: isSelected ? '#818cf8' : 'transparent',
        }}
        ref={(el) => {
          if (template.id) {
            setDraggableAnnotationParentRef(template.id, el)
          }
        }}
      >
        <Resizable
          size={
            containerDimensions.width > 0 && containerDimensions.height > 0
              ? containerDimensions
              : undefined
          }
          lockAspectRatio
          onResizeStart={(event, _, ref) => {
            event.stopPropagation()
            const width = ref.clientWidth
            const height = ref.clientHeight
            if (setIsResizing) setIsResizing(true)
            setContainerDimensions({ width, height })
          }}
          onResize={(event, direction, ref) => {
            event.stopPropagation()

            const newWidth = ref.clientWidth
            const newHeight = ref.clientHeight
            const widthDiff = newWidth - containerDimensions.width
            const heightDiff = newHeight - containerDimensions.height

            const deltaUpdates = {
              topLeft: { x: -widthDiff, y: -heightDiff },
              bottomLeft: { x: -widthDiff, y: 0 },
              topRight: { x: 0, y: -heightDiff },
              bottomRight: { x: 0, y: 0 },
            }

            setContainerDimensions({
              width: newWidth,
              height: newHeight,
            })

            setPosition((prevPosition) => ({
              ...prevPosition,
              delta: {
                x: prevPosition.delta.x + (deltaUpdates[direction]?.x || 0),
                y: prevPosition.delta.y + (deltaUpdates[direction]?.y || 0),
              },
            }))
          }}
          onResizeStop={(event) => {
            event.stopPropagation()
            if (setIsResizing) setIsResizing(false)
            if (!videoRef.current) return

            if (
              containerDimensions.width < 150 ||
              containerDimensions.height < 45
            )
              return

            updateVideoSizeHandler(
              {
                width: containerDimensions.width,
                height: containerDimensions.height,
              },
              {
                x: (position?.x || 0) + delta.x,
                y: (position?.y || 0) + delta.y,
              },
            )
              .then(() => {
                selectElement({
                  documentPageId,
                  id: template?.id as string,
                  isRemovable: true,
                  type: dataType,
                })
              })
              .catch((error) => {
                console.error('Error updating image size:', error)
              })
          }}
          handleClasses={{
            topRight: !isReadOnly && isSelected ? 'resize-handle' : '',
            topLeft: !isReadOnly && isSelected ? 'resize-handle' : '',
            bottomRight: !isReadOnly && isSelected ? 'resize-handle' : '',
            bottomLeft: !isReadOnly && isSelected ? 'resize-handle' : '',
          }}
          enable={
            isReadOnly
              ? false
              : {
                  topRight: true,
                  topLeft: true,
                  bottomRight: true,
                  bottomLeft: true,
                  top: false,
                  right: false,
                  bottom: false,
                  left: false,
                }
          }
        >
          <div
            ref={setNodeRef}
            className={cn(
              'flex flex-1 flex-col justify-center items-center h-full w-full p-2',
            )}
          >
            <div className="h-full w-full relative">
              {!aspectRatio && <Spinner className="absolute" />}
              <ErrorBoundary fallback={<div>Failed to load Video</div>}>
                <div
                  className={cn(
                    'h-full cursor-move flex justify-center flex-col items-center invisible',
                    {
                      'cursor-move': !isReadOnly,
                      'cursor-default': isReadOnly,
                      visible: !!aspectRatio,
                    },
                  )}
                >
                  <VideoPlayer
                    onVideoLoaded={onVideoLoaded}
                    src={downloadUrl}
                    ref={videoRef}
                    className={cn('max-h-full h-auto w-auto', {
                      'cursor-pointer': !isReadOnly,
                      'cursor-default': isReadOnly,
                    })}
                    onPointerDown={(e) => {
                      if (template) {
                        multiSelectKeydownHandler(e as any, template)
                      }
                    }}
                    onPointerUp={(e) => {
                      multiSelectKeyupHandler(e)
                    }}
                    data-testid="draggable-video"
                    onClick={onVideoPlayerClick}
                  />
                </div>
              </ErrorBoundary>
            </div>
          </div>
        </Resizable>
      </div>
    </OptionsContextMenu>
  )
}
